import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Shield, Users, Smartphone, Zap, Code, Database, Server, MessageSquare, Video } from 'lucide-react';

const CompanyPortfolio = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white font-sans overflow-x-hidden">
            {/* Header */}
            <header className="fixed top-0 w-full z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700/50">
                <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-tr from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-6 transition-transform">
                            <span className="text-white font-black text-xl tracking-tighter">AM</span>
                        </div>
                        <span className="text-xl sm:text-2xl font-black tracking-tight bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                            AVENTE MATNITCH
                        </span>
                    </div>
                    <nav className="hidden md:flex items-center space-x-8">
                        <a href="#about" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">About</a>
                        <a href="#services" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Services</a>
                        <a href="#products" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Products</a>
                        <Link to="/contact-us" className="text-sm font-medium text-gray-300 hover:text-white transition-colors">Contact</Link>
                    </nav>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
                <div className="absolute inset-0 max-w-7xl mx-auto">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl mix-blend-screen animate-pulse"></div>
                    <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl mix-blend-screen animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 text-center z-10">
                    <div className="inline-flex items-center px-4 py-2 rounded-full border border-gray-700/50 bg-gray-800/50 backdrop-blur-sm mb-8">
                        <span className="flex w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                        <span className="text-sm font-medium text-gray-300">Excellence in Software Development</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 leading-tight">
                        Building the Future <br className="hidden md:block" />
                        <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                            Through Technology.
                        </span>
                    </h1>
                    <p className="max-w-2xl mx-auto text-lg md:text-xl text-gray-400 mb-10 leading-relaxed">
                        AVENTE MATNITCH PRIVATE LIMITED specializes in creating cutting-edge software solutions. From scalable web architectures to impactful mobile applications, our mission is to drive digital innovation.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                            href="#products"
                            className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white transition-all bg-gradient-to-r from-blue-600 to-purple-600 rounded-full hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-purple-500/25"
                        >
                            Explore Our Products
                        </a>
                        <a
                            href="#services"
                            className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white transition-all bg-white/10 hover:bg-white/20 border border-white/10 rounded-full backdrop-blur-md"
                        >
                            Our Services
                        </a>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section id="services" className="py-20 bg-gray-900/50 border-y border-gray-800/50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-5xl font-black mb-6">Software Development Expertise</h2>
                        <p className="text-gray-400 text-lg">We deliver custom software that solves complex problems and drives business growth.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-800/40 p-8 rounded-3xl border border-gray-700/50">
                            <Code className="w-12 h-12 text-blue-400 mb-6" />
                            <h3 className="text-2xl font-bold mb-4">Custom Apps</h3>
                            <p className="text-gray-400">Tailored web and mobile applications designed with user experience and scalability in mind.</p>
                        </div>
                        <div className="bg-gray-800/40 p-8 rounded-3xl border border-gray-700/50">
                            <Server className="w-12 h-12 text-purple-400 mb-6" />
                            <h3 className="text-2xl font-bold mb-4">Cloud Solutions</h3>
                            <p className="text-gray-400">Robust cloud infrastructure and backend systems that ensure high availability and performance.</p>
                        </div>
                        <div className="bg-gray-800/40 p-8 rounded-3xl border border-gray-700/50">
                            <Database className="w-12 h-12 text-pink-400 mb-6" />
                            <h3 className="text-2xl font-bold mb-4">Data Systems</h3>
                            <p className="text-gray-400">Secure and efficient data management solutions that power intelligent applications.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section id="products" className="py-24 sm:py-32 relative">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
                        <h2 className="text-3xl md:text-5xl font-black mb-6">Our Flagship Products</h2>
                        <p className="text-gray-400 text-lg">Experience our successfully deployed solutions that are connecting millions every day.</p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Gupshup App */}
                        <div className="group relative rounded-3xl bg-gray-800/40 border border-gray-700/50 overflow-hidden hover:bg-gray-800/60 transition-colors flex flex-col">
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="p-8 md:p-10 flex-grow relative z-10">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                                    <span className="text-white font-bold text-3xl">G</span>
                                </div>
                                <h3 className="text-3xl font-black mb-4">Gupshup</h3>
                                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                                    Our premier voice and video calling platform. Connect anonymously with people across India, share moments, and find listeners who care.
                                </p>
                                <ul className="space-y-4 mb-10">
                                    <li className="flex items-center text-gray-300">
                                        <Shield className="w-5 h-5 mr-3 text-blue-400" /> 100% Anonymous & Secure
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Smartphone className="w-5 h-5 mr-3 text-purple-400" /> High-Quality Video Calls
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Users className="w-5 h-5 mr-3 text-pink-400" /> Verified Compassionate Listeners
                                    </li>
                                </ul>
                            </div>
                            <div className="p-8 pt-0 relative z-10">
                                <Link
                                    to="/gupshup-app"
                                    className="inline-flex w-full justify-center items-center font-bold text-white bg-white/10 hover:bg-white/20 px-6 py-4 rounded-xl backdrop-blur-md transition-colors"
                                >
                                    Visit Gupshup <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                            </div>
                        </div>

                        {/* Dakhla Video Call App */}
                        <div className="group relative rounded-3xl bg-gray-800/40 border border-gray-700/50 overflow-hidden hover:bg-gray-800/60 transition-colors flex flex-col">
                            <div className="absolute inset-0 bg-gradient-to-br from-rose-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="p-8 md:p-10 flex-grow relative z-10">
                                <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-rose-500/20">
                                    <Video className="text-white w-8 h-8" />
                                </div>
                                <h3 className="text-3xl font-black mb-4">Dakhla</h3>
                                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                                    A high-quality 1-on-1 live video call application for real-time face-to-face connections, finding partner friends, and HD video chat.
                                </p>
                                <ul className="space-y-4 mb-10">
                                    <li className="flex items-center text-gray-300">
                                        <Video className="w-5 h-5 mr-3 text-rose-400" /> HD Live 1-on-1 Video Calling
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Shield className="w-5 h-5 mr-3 text-indigo-400" /> Verified User Profiles
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Zap className="w-5 h-5 mr-3 text-purple-400" /> Instant Global Video Match
                                    </li>
                                </ul>
                            </div>
                            <div className="p-8 pt-0 relative z-10">
                                <Link
                                    to="/dakhla-app"
                                    className="inline-flex w-full justify-center items-center font-bold text-white bg-gradient-to-r from-rose-600 to-indigo-600 hover:from-rose-500 hover:to-indigo-500 px-6 py-4 rounded-xl shadow-lg transition-colors cursor-pointer"
                                >
                                    Visit Dakhla App <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                            </div>
                        </div>

                        {/* TellMe App */}
                        <div className="group relative rounded-3xl bg-gray-800/40 border border-gray-700/50 overflow-hidden hover:bg-gray-800/60 transition-colors flex flex-col">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="p-8 md:p-10 flex-grow relative z-10">
                                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-500 to-fuchsia-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/20">
                                    <MessageSquare className="text-white w-8 h-8 fill-current" />
                                </div>
                                <h3 className="text-3xl font-black mb-4">TellMe</h3>
                                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                                    Our premier voice and video chat platform. Connect anonymously with users across India, find empathetic listeners, and share real-time conversations safely.
                                </p>
                                <ul className="space-y-4 mb-10">
                                    <li className="flex items-center text-gray-300">
                                        <Shield className="w-5 h-5 mr-3 text-indigo-400" /> 100% Anonymous & Private
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Video className="w-5 h-5 mr-3 text-fuchsia-400" /> High-Quality Video Calls
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Users className="w-5 h-5 mr-3 text-purple-400" /> Verified Caring Listeners
                                    </li>
                                </ul>
                            </div>
                            <div className="p-8 pt-0 relative z-10">
                                <Link
                                    to="/tellme-app"
                                    className="inline-flex w-full justify-center items-center font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 px-6 py-4 rounded-xl shadow-lg transition-colors cursor-pointer"
                                >
                                    Visit TellMe <ArrowRight className="ml-2 w-5 h-5" />
                                </Link>
                            </div>
                        </div>
                        {/* 
                        <div className="group relative rounded-3xl bg-gray-800/40 border border-gray-700/50 overflow-hidden hover:bg-gray-800/60 transition-colors flex flex-col">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="p-8 md:p-10 flex-grow relative z-10">
                                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                                    <span className="text-white font-bold text-3xl">G</span>
                                </div>
                                <h3 className="text-3xl font-black mb-4">Guplup</h3>
                                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                                    Connect quickly and safely. Guplup brings instant video and voice communication to a massive scale, seamlessly connecting individuals across borders.
                                </p>
                                <ul className="space-y-4 mb-10">
                                    <li className="flex items-center text-gray-300">
                                        <Globe className="w-5 h-5 mr-3 text-indigo-400" /> Worldwide Connectivity
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Smartphone className="w-5 h-5 mr-3 text-cyan-400" /> Crystal Clear Audio
                                    </li>
                                </ul>
                            </div>
                            <div className="p-8 pt-0 relative z-10">
                                <button
                                    className="inline-flex w-full justify-center items-center font-bold text-white bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-500 hover:to-cyan-500 px-6 py-4 rounded-xl shadow-lg transition-colors cursor-pointer"
                                >
                                    Coming Soon <ArrowRight className="ml-2 w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="group relative rounded-3xl bg-gray-800/40 border border-gray-700/50 overflow-hidden hover:bg-gray-800/60 transition-colors flex flex-col">
                            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className="p-8 md:p-10 flex-grow relative z-10">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-yellow-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                                    <Users className="text-white w-8 h-8" />
                                </div>
                                <h3 className="text-3xl font-black mb-4">LiveGup</h3>
                                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                                    The ultimate live streaming experience. Broadcast yourself, discover talented creators, and engage with fans in real-time.
                                </p>
                                <ul className="space-y-4 mb-10">
                                    <li className="flex items-center text-gray-300">
                                        <Zap className="w-5 h-5 mr-3 text-green-400" /> Low Latency Streams
                                    </li>
                                    <li className="flex items-center text-gray-300">
                                        <Users className="w-5 h-5 mr-3 text-yellow-400" /> Community Events
                                    </li>
                                </ul>
                            </div>
                            <div className="p-8 pt-0 relative z-10">
                                <button
                                    className="inline-flex w-full justify-center items-center font-bold text-gray-900 bg-gradient-to-r from-green-400 to-yellow-400 hover:from-green-300 hover:to-yellow-300 px-6 py-4 rounded-xl shadow-lg transition-colors cursor-pointer"
                                >
                                    Coming Soon <ArrowRight className="ml-2 w-5 h-5" />
                                </button>
                            </div>
                        </div> */}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-950 pt-20 pb-10 border-t border-gray-800/50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
                        <div className="col-span-1 md:col-span-2">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-8 h-8 bg-gradient-to-tr from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">AM</span>
                                </div>
                                <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                                    AVENTE MATNITCH
                                </span>
                            </div>
                            <p className="text-gray-400 max-w-sm leading-relaxed mb-4">
                                AVENTE MATNITCH PRIVATE LIMITED is a premium software development company building applications that shape the modern world.
                            </p>
                            <p className="text-gray-500 text-sm">
                                Registered in India.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-white font-bold mb-6 tracking-wide">Company</h4>
                            <ul className="space-y-4">
                                <li><a href="#services" className="text-gray-400 hover:text-white transition-colors">Services</a></li>
                                <li><a href="#products" className="text-gray-400 hover:text-white transition-colors">Products</a></li>
                                <li><Link to="/contact-us" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-white font-bold mb-6 tracking-wide">Legal & Support</h4>
                            <ul className="space-y-4">
                                <li><Link to="/terms-and-conditions" className="text-gray-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
                                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                                <li><Link to="/child-safety" className="text-gray-400 hover:text-red-400 transition-colors">Child Safety Center</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-gray-800/50 flex flex-col md:flex-row items-center justify-between text-gray-500 text-sm">
                        <p>&copy; {new Date().getFullYear()} AVENTE MATNITCH PRIVATE LIMITED. All rights reserved.</p>
                        <div className="flex items-center space-x-4 mt-4 md:mt-0">
                            <Globe className="w-4 h-4" />
                            <span>Global Software Development</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default CompanyPortfolio;
